import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import CopyButton from '../../components/shared/CopyButton'
import ErrorBanner from '../../components/shared/ErrorBanner'
import { payloadResult } from '../../helpers/pipelines'
import { errorMessage } from '../../services/axios/errorMessage'
import PipelineService from '../../services/pipelineService'
import { FONT, RADIUS, SURFACE, TEXT } from '../../theme/tokens'

/**
 * The integration payload of an event type: the sample event a service sends
 * to trigger the event type's pipelines (GET /comms/v2/get_payload), shown
 * indented with a copy button. Open while `eventType` is set.
 *
 * The service answers 200 with `status: FAILURE` when the event type has no
 * active pipeline; the old dialog then showed an empty box, this one says so.
 */
const PayloadDialog = ({ eventType, onClose }) => {
    const dispatch = useDispatch()
    // loading | ready (with text) | error (with message and whether the
    // service itself refused to build it)
    const [result, setResult] = useState({ state: 'loading' })

    useEffect(() => {
        if (!eventType) return undefined
        let current = true
        setResult({ state: 'loading' })
        dispatch(PipelineService.getPipelinePayload(eventType)).then((resp) => {
            if (!current) return
            if (resp.error) {
                setResult({
                    state: 'error',
                    message: errorMessage({ data: resp.payload }),
                })
                return
            }
            const read = payloadResult(resp.payload?.data)
            setResult(
                read.payload
                    ? {
                          state: 'ready',
                          text: JSON.stringify(read.payload, null, 2),
                      }
                    : { state: 'error', message: read.error, refused: true }
            )
        })
        // A late answer for an event type closed in the meantime is dropped.
        return () => {
            current = false
        }
    }, [eventType, dispatch])

    return (
        <Dialog
            open={!!eventType}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            aria-labelledby="payload-dialog-title"
        >
            <DialogTitle id="payload-dialog-title">
                Integration payload
            </DialogTitle>
            <DialogContent>
                <Typography variant="overline" sx={{ color: TEXT.muted }}>
                    Event type
                </Typography>
                <Typography
                    sx={{
                        fontFamily: FONT.mono,
                        color: TEXT.primary,
                        mb: 1,
                        wordBreak: 'break-all',
                    }}
                >
                    {eventType}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ color: TEXT.secondary, mb: 2 }}
                >
                    {
                        "The event a service sends to trigger this event type's pipelines. Each value describes what to put there."
                    }
                </Typography>
                {result.state === 'loading' && (
                    <Box
                        role="status"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 4,
                        }}
                    >
                        <CircularProgress size={18} />
                        <Typography variant="body2" sx={{ color: TEXT.muted }}>
                            Loading the payload…
                        </Typography>
                    </Box>
                )}
                {result.state === 'error' && (
                    <>
                        <ErrorBanner
                            message={`The payload could not be loaded: ${result.message}`}
                        />
                        {result.refused && (
                            <Typography
                                variant="body2"
                                sx={{ color: TEXT.muted, mt: 1 }}
                            >
                                The service builds it only while the event type
                                has an active pipeline with a template.
                            </Typography>
                        )}
                    </>
                )}
                {result.state === 'ready' && (
                    <Box sx={{ position: 'relative' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '6px',
                                right: '6px',
                            }}
                        >
                            <CopyButton text={result.text} />
                        </Box>
                        <Box
                            component="pre"
                            data-testid="payload-text"
                            sx={{
                                m: 0,
                                p: 2,
                                pr: 6,
                                maxHeight: '60vh',
                                overflow: 'auto',
                                borderRadius: `${RADIUS.sm}px`,
                                border: `1px solid ${SURFACE.border}`,
                                // A sunken well, so the code stands apart
                                // from the dialog in both themes.
                                background: SURFACE.sunken,
                                color: TEXT.secondary,
                                typography: 'body2',
                                fontFamily: FONT.mono,
                                lineHeight: 1.6,
                            }}
                        >
                            {result.text}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant="outlined" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

PayloadDialog.propTypes = {
    // The event type whose payload to show; the dialog is closed without one.
    eventType: PropTypes.string,
    onClose: PropTypes.func.isRequired,
}

export default PayloadDialog
